import { child, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'
import { proxyMap } from 'valtio/utils'

import * as firebaseConnection from '../firebaseConnection'
import { useIsFeed } from '../../Speakers/blocs/useSpeakers'

const ROOT_NODE = 'muteStates'

type MuteState = {
  isMuted?: boolean
  isPlaybackMuted?: boolean
  initiatedBy?: number
}

const feedMuteStates = proxy<{
  participants: Map<number, MuteState>
}>({
  participants: proxyMap(),
})

firebaseConnection.runWhenReady((connection) => {
  onValue(child(connection.room, ROOT_NODE), (snapshot) => {
    const data = (snapshot.val() ?? {}) as Record<string, MuteState>

    const nextParticipantIds = new Set<number>()
    Object.entries(data).forEach(([participantId, muteState]) => {
      const numericId = Number(participantId)
      nextParticipantIds.add(numericId)
      const existing = feedMuteStates.participants.get(numericId)

      if (
        !existing ||
        existing.isMuted !== muteState.isMuted ||
        existing.isPlaybackMuted !== muteState.isPlaybackMuted ||
        existing.initiatedBy !== muteState.initiatedBy
      ) {
        feedMuteStates.participants.set(numericId, muteState)
      }
    })

    Array.from(feedMuteStates.participants.keys()).forEach((id) => {
      if (!nextParticipantIds.has(id)) {
        feedMuteStates.participants.delete(id)
      }
    })
  })
})

const getFeedMuteState = (participantId: number) =>
  (feedMuteStates.participants.get(participantId)?.isMuted ?? false) ||
  (feedMuteStates.participants.get(participantId)?.isPlaybackMuted ?? false)

const useIsFeedMuted = (participantId: number) => {
  const participant = useSnapshot(feedMuteStates.participants).get(
    participantId
  )
  const isFeed = useIsFeed(participantId)

  if (!isFeed) {
    return false
  }

  return (
    (participant?.isMuted ?? false) || (participant?.isPlaybackMuted ?? false)
  )
}

export { type MuteState, getFeedMuteState, useIsFeedMuted }
